import { storyblokEditable, StoryblokServerComponent, StoryblokStory, type ISbStoryData } from '@storyblok/react/rsc';
import { I18N_CONF } from '@funkey/locales/config';
import { StoryblokUtils } from '~/storyblok/utils';
import type { SB_MultilinkProps } from './types.generated';
import type { SearchParams } from 'nuqs/server';

/** A rewrite of `@storyblok/react/rsc#SbBlokData` because we don't want the {[k: string]: any} type */
type StoryblokBlokkData = {
  _uid?: string;
  component?: string;
  _editable?: string;
};

/** Use this instead of `storyblokEditable()`.
 *
 * This wrapper function only exists because Storyblok have shit types.
 * They require index signatures on all of the props, which is exactly what we don't want.
 *
 * That way we can accidentally misspell a prop like `uid` instead of `_uid` and typescript would be happy. */
export function storyblokBridgeAttributes(blok: StoryblokBlokkData) {
  return storyblokEditable(blok);
}
type StoryblokRenderCtx = {
  locale: I18N_CONF.Locale;
  searchParams: Promise<SearchParams>;
};
export type StoryblokBlokProps<Props extends {
  _uid: string;
  /** Blok component name */
  component: string;
  _editable?: string;
}> = {
  ctx: StoryblokRenderCtx;
  blok: Props;
};

/** Rendering entry point of a storyblok story */
export const StoryblokPage: React.FC<{
  ctx: StoryblokRenderCtx;
  story: ISbStoryData<unknown>;
}> = StoryblokStory;

/** A rendering utility to render nested Storyblok blocks */
export const StoryblokRenderer: React.FC<{
  ctx: StoryblokRenderCtx;
  blocks?: Array<StoryblokBlokkData>;
}> = props => props.blocks?.map(blok => <StoryblokServerComponent key={blok._uid} blok={blok} ctx={props.ctx} />); // prettier-ignore

/** Storyblok slugs are sometimes stored with a leading slash, so concatenating them onto a `/`
 * yields `//en/contact`, which browsers resolve as the protocol-relative `https://en/contact`.
 * Collapse those leading slashes, unless the first segment is a hostname (`//cdn.example.com/x`). */
function normalizeStoryblokHref(href: string): string {
  if (!href.startsWith('//')) return href;
  const withoutLeadingSlashes = href.replace(/^\/+/, '');
  if (withoutLeadingSlashes.split('/')[0]?.includes('.')) return href;
  return `/${withoutLeadingSlashes}`;
}

/** Storyblok stores every non-default translation under a language folder, so a story slug reads
 * `en/contact`, not `contact`. Concatenating our own locale prefix onto that yields
 * `/en-BE/en/contact`, which 404s. Drop the Storyblok language segment first. */
function stripStoryblokLanguagePrefix(path: string): string {
  const [, firstSegment, rest] = /^\/([^/]+)(\/.*)?$/.exec(path) ?? [];
  if (!firstSegment) return path;
  return StoryblokUtils.LOCALES.some(sbLocale => sbLocale !== 'default' && sbLocale === firstSegment.toLowerCase()) ? rest || '/' : path;
}
export function transformStoryblokLink(link: SB_MultilinkProps, currentLocale?: I18N_CONF.Locale): {
  href: string;
  target?: React.HTMLAttributeAnchorTarget;
  children?: React.ReactNode;
} {
  switch (link.linktype) {
    case 'url':
      return {
        /** @TODO: check if this is correct. Legacy behaviour */
        target: link.target || link.url?.startsWith('http') ? '_blank' : '_self',
        href: normalizeStoryblokHref(link.url || link.cached_url!),
        children: link.title
      };
    case 'email':
      return {
        target: link.target ?? '_blank',
        href: link.email ? `mailto:${link.email}` : link.cached_url,
        children: link.email
      };
    case 'story':
      const baseHref = normalizeStoryblokHref(`/${link.story?.full_slug || link.cached_url || ''}`);
      return {
        target: link.target ?? '_self',
        href: currentLocale ? `${I18N_CONF.getLocalePathPrefix(currentLocale)}${stripStoryblokLanguagePrefix(baseHref)}` : baseHref,
        children: link.story?.name
      };
    case 'asset':
      return {
        href: (link.url || link.cached_url) ?? '#',
        target: link.target,
        children: link.url
      };
    default:
      // link.linktype satisfies never;
      throw new Error('UNREACHABLE');
  }
}